import type { Firestore } from 'firebase/firestore'
import type { FirebaseApp } from 'firebase/app'
import {
  LEADERBOARD_MIN_RESULTS,
  HARDEST_WORD_LOOKBACK_DAYS,
  TIME_BUCKET_SECONDS,
  TIME_MIN_SECONDS,
  TIME_MAX_SECONDS,
  GLOBAL_GUESS_DISTRIBUTION,
  GLOBAL_FAILED,
} from '../constants/settings'

// All cross-player stats live behind this module. The rest of the app only
// ever sees plain data or null — Firebase never leaks into components, and
// every call degrades silently when the backend is not configured or offline.

export const TIME_BUCKET_COUNT =
  (TIME_MAX_SECONDS - TIME_MIN_SECONDS) / TIME_BUCKET_SECONDS // 15

export type DayAggregate = {
  day: number
  solution: string
  total: number
  failed: number
  guessCounts: number[] // index 0 = solved in 1 … index 5 = solved in 6
  // timeBuckets[g][b]: solved in g+1 guesses within time bucket b; extremes
  // (<15 s, >4 min) are deliberately not counted anywhere.
  timeBuckets: number[][]
}

export type HardestWord = {
  solution: string
  solveRate: number
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

export const isLeaderboardEnabled = () =>
  Boolean(firebaseConfig.apiKey && firebaseConfig.projectId)

const submittedDayKey = 'leaderboardSubmittedDay'
const lastResultKey = 'leaderboardLastResult'
const startedKey = 'gameStartTs'
const invalidKey = 'invalidGuesses'

export type LastResult = {
  day: number
  attempts: number
  duration: number
  solved: boolean
}

// ---------------------------------------------------------------- local state

// Called on the first typed letter of a fresh game; survives reloads.
export const markGameStarted = (day: number) => {
  try {
    const raw = localStorage.getItem(startedKey)
    const cur = raw ? JSON.parse(raw) : null
    if (!cur || cur.day !== day) {
      localStorage.setItem(startedKey, JSON.stringify({ day, ts: Date.now() }))
    }
  } catch (e) {}
}

export const getPlayDuration = (day: number): number => {
  try {
    const raw = localStorage.getItem(startedKey)
    const cur = raw ? JSON.parse(raw) : null
    if (!cur || cur.day !== day) return 0
    return Math.max(0, Math.round((Date.now() - cur.ts) / 1000))
  } catch (e) {
    return 0
  }
}

// Words the dictionary rejected ("Žodis nerastas") — kept per day, capped.
export const recordInvalidGuess = (day: number, word: string) => {
  try {
    const raw = localStorage.getItem(invalidKey)
    const cur = raw ? JSON.parse(raw) : null
    const words: string[] = cur && cur.day === day ? cur.words : []
    if (!words.includes(word) && words.length < 40) {
      words.push(word)
    }
    localStorage.setItem(invalidKey, JSON.stringify({ day, words }))
  } catch (e) {}
}

const getInvalidGuesses = (day: number): string[] => {
  try {
    const raw = localStorage.getItem(invalidKey)
    const cur = raw ? JSON.parse(raw) : null
    return cur && cur.day === day ? cur.words : []
  } catch (e) {
    return []
  }
}

export const getLastResult = (day: number): LastResult | null => {
  try {
    const raw = localStorage.getItem(lastResultKey)
    const cur = raw ? JSON.parse(raw) : null
    return cur && cur.day === day ? cur : null
  } catch (e) {
    return null
  }
}

// ------------------------------------------------------------------ firebase

export const getFirebaseApp = async (): Promise<FirebaseApp> => {
  const { initializeApp, getApps, getApp } = await import('firebase/app')
  return getApps().length ? getApp() : initializeApp(firebaseConfig)
}

let dbPromise: Promise<{ db: Firestore; uid: string }> | null = null

const getDb = () => {
  if (!dbPromise) {
    dbPromise = (async () => {
      const app = await getFirebaseApp()
      const [{ getAuth, signInAnonymously }, firestore] = await Promise.all([
        import('firebase/auth'),
        import('firebase/firestore'),
      ])
      const auth = getAuth(app)
      const uid = auth.currentUser
        ? auth.currentUser.uid
        : (await signInAnonymously(auth)).user.uid
      return { db: firestore.getFirestore(app), uid }
    })()
  }
  return dbPromise
}

export const timeBucket = (duration: number): number | null => {
  if (duration < TIME_MIN_SECONDS || duration > TIME_MAX_SECONDS) return null
  return Math.min(
    Math.floor((duration - TIME_MIN_SECONDS) / TIME_BUCKET_SECONDS),
    TIME_BUCKET_COUNT - 1
  )
}

// Records a finished game in one batch: a per-day lock (one play per player
// per day), a write-once result document (ID carries seconds-of-day so the
// console lists plays chronologically) and +1s on the day's public aggregate.
export const submitResult = async (
  day: number,
  solution: string,
  words: string[],
  solved: boolean,
  hard: boolean
) => {
  const attemptsLocal = words.length
  const durationLocal = getPlayDuration(day)
  // Record the result locally first so the achievement (against today or the
  // global baseline) shows even if the network submit fails.
  try {
    localStorage.setItem(
      lastResultKey,
      JSON.stringify({
        day,
        attempts: attemptsLocal,
        duration: durationLocal,
        solved,
      })
    )
  } catch (e) {}

  if (!isLeaderboardEnabled()) return
  try {
    if (localStorage.getItem(submittedDayKey) === String(day)) return
    const duration = durationLocal
    const { db, uid } = await getDb()
    const { doc, writeBatch, increment, serverTimestamp } = await import(
      'firebase/firestore'
    )
    const attempts = words.length
    const now = new Date()
    const secondsOfDay =
      now.getUTCHours() * 3600 + now.getUTCMinutes() * 60 + now.getUTCSeconds()
    const sss = String(secondsOfDay).padStart(5, '0')

    const bump: Record<string, unknown> = {
      day,
      solution,
      total: increment(1),
      [solved ? `g${attempts}` : 'failed']: increment(1),
    }
    const bucket = timeBucket(duration)
    if (solved && bucket !== null) {
      bump[`t${attempts}_${bucket}`] = increment(1)
    }

    const batch = writeBatch(db)
    batch.set(doc(db, 'locks', `${day}_${uid}`), { day })
    batch.set(doc(db, 'results', `${day}_${sss}_${uid}`), {
      day,
      guesses: attempts,
      words,
      invalid: getInvalidGuesses(day),
      solved,
      hard,
      duration,
      ts: serverTimestamp(),
    })
    batch.set(doc(db, 'aggregates', String(day)), bump, { merge: true })
    await batch.commit()
    localStorage.setItem(submittedDayKey, String(day))

    // Best-effort: bump the global review queue for each unrecognised word.
    // Separate from the critical batch — a failure here never blocks a result.
    const invalidWords = getInvalidGuesses(day)
    if (invalidWords.length > 0) {
      const { doc, setDoc, increment } = await import('firebase/firestore')
      await Promise.all(
        invalidWords.map((w) => {
          const id = w.toLocaleLowerCase('lt')
          return setDoc(
            doc(db, 'invalidWords', id),
            { word: id, count: increment(1) },
            { merge: true }
          ).catch(() => {})
        })
      )
    }
  } catch (e) {
    console.warn('leaderboard: submit failed', e)
  }
}

const readAggregate = async (
  db: Firestore,
  day: number
): Promise<DayAggregate | null> => {
  const { doc, getDoc } = await import('firebase/firestore')
  const snap = await getDoc(doc(db, 'aggregates', String(day)))
  if (!snap.exists()) return null
  const d = snap.data()
  return {
    day,
    solution: d.solution ?? '',
    total: d.total ?? 0,
    failed: d.failed ?? 0,
    guessCounts: Array.from({ length: 6 }, (_, i) => d[`g${i + 1}`] ?? 0),
    timeBuckets: Array.from({ length: 6 }, (_, g) =>
      Array.from(
        { length: TIME_BUCKET_COUNT },
        (_, b) => d[`t${g + 1}_${b}`] ?? 0
      )
    ),
  }
}

const aggregateCache = new Map<number, Promise<DayAggregate | null>>()

export const fetchDayAggregate = (
  day: number
): Promise<DayAggregate | null> => {
  if (!isLeaderboardEnabled()) return Promise.resolve(null)
  if (!aggregateCache.has(day)) {
    aggregateCache.set(
      day,
      (async () => {
        try {
          const { db } = await getDb()
          return await readAggregate(db, day)
        } catch (e) {
          console.warn('leaderboard: aggregate fetch failed', e)
          aggregateCache.delete(day)
          return null
        }
      })()
    )
  }
  return aggregateCache.get(day)!
}

// Five achievement tiers for the daily percentile, top tier at 85 %+.
const TIERS = [
  { min: 85, emoji: '\u{1F3C6}', tier: 4 }, // trophy
  { min: 70, emoji: '\u{1F947}', tier: 3 }, // gold medal
  { min: 55, emoji: '\u{1F948}', tier: 2 }, // silver medal
  { min: 40, emoji: '\u{1F949}', tier: 1 }, // bronze medal
  { min: 0, emoji: '\u{1F44F}', tier: 0 }, // clapping hands
]

export const achievementFor = (percent: number) =>
  TIERS.find((t) => percent >= t.min) ?? TIERS[TIERS.length - 1]

export const achievementEmoji = (percent: number): string =>
  achievementFor(percent).emoji

// Tailwind text colour for the headline percent, by tier.
export const TIER_COLORS = [
  'text-indigo-600 dark:text-indigo-400', // 0 applause
  'text-amber-700 dark:text-amber-500', // 1 bronze
  'text-slate-500 dark:text-slate-300', // 2 silver
  'text-yellow-600 dark:text-yellow-400', // 3 gold
  'text-yellow-500 dark:text-yellow-300', // 4 trophy
]

// Share of today's other players this player beat: more guesses or a failed
// game always count; on equal guesses, slower in-range time buckets count.
// Null until the day has enough results to be meaningful.
export const percentileBeaten = (
  agg: DayAggregate,
  attempts: number,
  solved: boolean,
  duration: number
): number | null => {
  if (!solved || agg.total < LEADERBOARD_MIN_RESULTS) return null
  // Players strictly better than me: fewer guesses, or same guesses but a
  // strictly faster time bucket. Everyone else (worse or tied) counts in my
  // favour — the upper end of my range, so being uniquely best is 100 %.
  let better = agg.guessCounts.slice(0, attempts - 1).reduce((a, b) => a + b, 0)
  const myBucket = timeBucket(duration)
  if (myBucket !== null) {
    better += agg.timeBuckets[attempts - 1]
      .slice(0, myBucket)
      .reduce((a, b) => a + b, 0)
  }
  const others = agg.total - 1
  if (others <= 0) return null
  return Math.max(
    0,
    Math.min(100, Math.round((100 * (others - better)) / others))
  )
}

// Synthetic aggregate from the global baseline, used when a day has too few
// real results. No time buckets, so ranking falls back to guess count only.
export const globalBaselineAggregate = (): DayAggregate => ({
  day: -1,
  solution: '',
  total: GLOBAL_GUESS_DISTRIBUTION.reduce((a, b) => a + b, 0) + GLOBAL_FAILED,
  failed: GLOBAL_FAILED,
  guessCounts: [...GLOBAL_GUESS_DISTRIBUTION],
  timeBuckets: Array.from({ length: 6 }, () =>
    Array(TIME_BUCKET_COUNT).fill(0)
  ),
})

export const hasEnoughToday = (agg: DayAggregate | null): boolean =>
  agg !== null && agg.total >= LEADERBOARD_MIN_RESULTS

// Lowest solve rate over the previous fully played days (today is still
// accumulating, so it is excluded).
export const fetchHardestWord = async (
  today: number
): Promise<HardestWord | null> => {
  if (!isLeaderboardEnabled()) return null
  try {
    const days = Array.from(
      { length: HARDEST_WORD_LOOKBACK_DAYS },
      (_, i) => today - 1 - i
    ).filter((d) => d >= 0)
    const aggs = (await Promise.all(days.map((d) => fetchDayAggregate(d))))
      .filter((a): a is DayAggregate => a !== null)
      .filter((a) => a.total >= LEADERBOARD_MIN_RESULTS && a.solution)
    if (aggs.length === 0) return null
    const rated = aggs.map((a) => ({
      solution: a.solution,
      solveRate: (a.total - a.failed) / a.total,
    }))
    rated.sort((a, b) => a.solveRate - b.solveRate)
    return rated[0]
  } catch (e) {
    console.warn('leaderboard: hardest word fetch failed', e)
    return null
  }
}
